import { useEffect, useMemo, useState } from "react";
import { formatUzPhone } from "../phone";
import {
  api,
  daysUntil,
  fmtMoney,
  type Revenue,
  type Stats,
  type WorkshopRow,
} from "../api";
import { KpiCard, KpiSkeleton } from "../components/KpiCard";
import { HealthBadge, TariffBadge } from "../components/Badge";
import {
  IconWrench, IconActivity, IconClock, IconAlert, IconKey,
  IconPhone, IconPlus, IconArrowRight,
} from "../components/icons";
import type { Location } from "../routing";

const HEALTH_ORDER: Record<string, number> = { red: 0, yellow: 1, green: 2 };

export function Overview({
  onNavigate,
  onOpenCreate,
  onOpenNewCode,
}: {
  onNavigate: (loc: Location) => void;
  onOpenCreate: () => void;
  onOpenNewCode: () => void;
}) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [rows, setRows] = useState<WorkshopRow[] | null>(null);
  const [revenue, setRevenue] = useState<Revenue | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, w, rev] = await Promise.all([
          api.stats(),
          api.workshops(),
          api.revenue(),
        ]);
        setStats(s);
        setRows(w);
        setRevenue(rev);
      } catch (e) { setErr((e as Error).message); }
    })();
  }, []);

  // Salomatlik sanog'i — bitta manbadan (ustaxonalar ro'yxati), qo'shimcha so'rovsiz.
  const counts = useMemo(() => {
    const c = { red: 0, yellow: 0, green: 0 };
    for (const r of rows ?? []) c[r.health] += 1;
    return c;
  }, [rows]);

  // Kunlik "qo'ng'iroq ro'yxati" — faqat qizil + sariq, qizil birinchi, keyin
  // oxirgi ishdan beri o'tgan kun kamayish tartibida.
  const risk = useMemo(
    () =>
      (rows ?? [])
        .filter((r) => r.health === "red" || r.health === "yellow")
        .sort((a, b) => {
          const h = HEALTH_ORDER[a.health] - HEALTH_ORDER[b.health];
          if (h !== 0) return h;
          return (b.days_since_last_work ?? 1e9) - (a.days_since_last_work ?? 1e9);
        }),
    [rows]
  );

  function openDetail(id: number) {
    onNavigate({ route: "workshop-detail", id });
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Bosh sahifa</h1>
          <p className="page-sub">Bugun kimga qo'ng'iroq qilish kerak</p>
        </div>
        <div className="actions">
          <button className="btn ghost" onClick={onOpenNewCode}><IconPlus /> Yangi kod</button>
          <button className="btn" onClick={onOpenCreate}><IconPlus /> Yangi ustaxona</button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {/* Console: MRR readout + salomatlik gauge klasteri */}
      <div className="console">
        <div className="mrr-panel">
          <div className="label">Oylik daromad (MRR)</div>
          <div className="mrr-headline">{revenue ? fmtMoney(revenue.mrr) : "…"}</div>
          {revenue && (
            <div className="mrr-note">
              {revenue.active_trials} faol sinov · taxminiy pol:{" "}
              {fmtMoney(revenue.trial_floor_estimate)} (MRR ga kirmaydi)
            </div>
          )}
        </div>
        <div className="gauges">
          <div className="gauge red">
            <span className="gauge-label"><span className="health-dot" /> Xavf ostida</span>
            <span className="gauge-value">{rows ? counts.red : "…"}</span>
          </div>
          <div className="gauge yellow">
            <span className="gauge-label"><span className="health-dot" /> Sust</span>
            <span className="gauge-value">{rows ? counts.yellow : "…"}</span>
          </div>
          <div className="gauge green">
            <span className="gauge-label"><span className="health-dot" /> Sog'lom</span>
            <span className="gauge-value">{rows ? counts.green : "…"}</span>
          </div>
        </div>
      </div>

      {stats ? (
        <div className="kpis">
          <KpiCard tone="primary" icon={<IconWrench />} label="Jami ustaxonalar" value={stats.total_workshops} delta={`+${stats.new_this_month} bu oy`} deltaTone="up" />
          <KpiCard icon={<IconActivity />} label="Faol" value={stats.active_workshops} />
          <KpiCard icon={<IconClock />} label="Sinov" value={stats.trial_workshops} delta={stats.trial_ending_soon > 0 ? `${stats.trial_ending_soon} ta tugashga yaqin` : undefined} deltaTone="warn" />
          <KpiCard tone="danger" icon={<IconAlert />} label="Muddati tugagan" value={stats.expired_workshops} />
          <KpiCard icon={<IconKey />} label="Foydalanilmagan kodlar" value={stats.unused_codes} />
        </div>
      ) : (
        <div className="kpis">
          {Array.from({ length: 5 }).map((_, i) => <KpiSkeleton key={i} />)}
        </div>
      )}

      {/* Xavf ostidagi ustaxonalar — kunlik qo'ng'iroq navbati */}
      <div className="panel flush">
        <div className="panel-title" style={{ padding: "16px 20px 0" }}>
          <h2>Qo'ng'iroq navbati</h2>
          <button className="btn small ghost" onClick={() => onNavigate({ route: "workshops" })}>
            Hammasi <IconArrowRight />
          </button>
        </div>
        {rows === null ? (
          <div className="kpis" style={{ padding: 20, marginBottom: 0 }}>
            {Array.from({ length: 3 }).map((_, i) => <KpiSkeleton key={i} />)}
          </div>
        ) : risk.length === 0 ? (
          <div className="empty">Hamma ustaxona sog'lom — qo'ng'iroq navbati bo'sh.</div>
        ) : (
          <table className="table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Ustaxona</th>
                <th>Tarif</th>
                <th>Holat</th>
                <th>Sabab</th>
                <th>Telefon</th>
                <th className="num">Oxirgi ish</th>
                <th className="num">Trial</th>
              </tr>
            </thead>
            <tbody>
              {risk.map((r) => {
                const trialDays =
                  r.tariff === "trial" && !r.is_expired ? daysUntil(r.trial_end) : null;
                return (
                  <tr
                    key={r.id}
                    className="clickable call-row"
                    role="link"
                    tabIndex={0}
                    onClick={() => openDetail(r.id)}
                    onKeyDown={(e) => { if (e.key === "Enter") openDetail(r.id); }}
                  >
                    <td><strong>{r.name}</strong></td>
                    <td><TariffBadge tariff={r.tariff} /></td>
                    <td><HealthBadge health={r.health} reason={r.reason} /></td>
                    <td className="risk-reason">{r.reason}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {r.owner_phone ? (
                        <a className="call-tel" href={`tel:${r.owner_phone}`}>
                          <IconPhone /> {formatUzPhone(r.owner_phone)}
                        </a>
                      ) : "—"}
                    </td>
                    <td className="num">{r.days_since_last_work === null ? "—" : `${r.days_since_last_work} kun`}</td>
                    <td className="num">{trialDays !== null ? `${trialDays} kun` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
